import { format, parseISO } from "date-fns";
import { useNavigate } from "react-router-dom";

import { ArrowDown, ArrowUp, DocumentValidation } from "@/assets/icons";
import { Badge, Card } from "@/atoms";
import { BadgeStateEnum } from "@/enums";
import { AlfredTransactionDataInterface } from "@/interfaces";
import { formatCurrencyAmountHandler, generateValidClassNameHandler } from "@/lib/utils";
import { useAlfredChatContext } from "@/providers/alfred/chat";
import { ReconciliationStatusType, TransactionStatusType } from "@/types";
import { Button } from "@/ui/button";

const getLedgerStatusBadgeState = (status: TransactionStatusType) => {
    switch (status) {
    case "POSTED":
        return BadgeStateEnum.SUCCESS;
    case "PENDING":
        return BadgeStateEnum.WARNING;
    case "ERROR":
        return BadgeStateEnum.DANGER;
    default:
        return BadgeStateEnum.DEFAULT;
    }
};

const getReconciliationStatusTypeBadgeState = (status: ReconciliationStatusType | null | undefined) => {
    switch (status) {
    case "MATCHED":
        return BadgeStateEnum.SUCCESS;
    case "PARTIALLY_MATCHED":
    case "REVIEW":
    case "NOT_PROCESSED":
        return BadgeStateEnum.WARNING;
    case "UNMATCHED":
    case "EXCEPTIONS":
        return BadgeStateEnum.DANGER;
    default:
        return BadgeStateEnum.DEFAULT;
    }
};

const formatDate = (dateStr: string) => {
    try {
        return format(
            parseISO(dateStr),
            "MMMM d, yyyy",
        );
    } catch {
        return dateStr;
    }
};

export const AlfredTransactionCard = ({
    className,
    data,
} : {
    className?: string,
    data: AlfredTransactionDataInterface,
}) => {
    const navigate = useNavigate();
    const { sendMessage } = useAlfredChatContext();

    const handleViewDetails = () => {
        navigate(`/ledger/${data.id}`);
    };

    const handleAskAlfred = () => {
        sendMessage(`Tell me more about transaction ${data.reference || data.id}`);
    };

    const isDebit = data.type === "DEBIT";

    const headerContent = (
        <>
            <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                    <DocumentValidation className="size-4 text-gray-500" />
                    <span className="text-sm font-medium leading-tight">
                        {data.name}
                    </span>
                </div>
                <Badge state={getLedgerStatusBadgeState(data.ledgerStatus)}>
                    {data.ledgerStatus}
                </Badge>
            </div>
            <p className="text-xs text-gray-500">{formatDate(data.date)}</p>
        </>
    );

    const cardContent = (
        <>
            <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Amount</span>
                    <div className="flex items-center gap-1">
                        <span
                            className={generateValidClassNameHandler(
                                "text-lg font-semibold",
                                isDebit ? "text-red-600" : "text-green-600",
                            )}
                        >
                            {formatCurrencyAmountHandler(
                                data.amount,
                                data.currency,
                            )}
                        </span>
                        {isDebit ? <ArrowDown className="size-4 text-red-500" /> : <ArrowUp className="size-4 text-green-500" />}
                    </div>
                </div>

                <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Account</span>
                    <span className="text-sm">{data.accountName}</span>
                </div>

                {data.reference && (
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Reference</span>
                        <span className="font-mono text-sm">{data.reference}</span>
                    </div>
                )}

                {data.matchingStatus && (
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Matching Status</span>
                        <Badge state={getReconciliationStatusTypeBadgeState(data.matchingStatus)}>
                            {data.matchingStatus}
                        </Badge>
                    </div>
                )}
            </div>

            {/* Action buttons as part of content (Option 3) */}
            <div className="mt-3 flex gap-2 border-t pt-3">
                <Button
                    className="flex-1"
                    size="sm"
                    onClick={handleViewDetails}
                >
                    View Details
                </Button>
                <Button
                    className="flex-1"
                    size="sm"
                    variant="outline"
                    onClick={handleAskAlfred}
                >
                    Ask Alfred
                </Button>
            </div>
        </>
    );

    return (
        <Card
            content={cardContent}
            contentClassName="pb-3 pt-0"
            headerClassName="pb-2"
            otherHeaderData={headerContent}
            className={generateValidClassNameHandler(
                "w-full border shadow-sm",
                className,
            )}
        />
    );
};
